import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  VStack,
  Heading,
  Select,
  Text,
  useColorModeValue,
  Card,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Spinner,
} from '@chakra-ui/react';
import { motion, AnimatePresence } from 'framer-motion';
import { GoogleGenerativeAI } from "@google/generative-ai";
import ReactMarkdown from 'react-markdown';
import { FaExclamationTriangle } from 'react-icons/fa';

// Country options - you can expand this list
const countries = [
  { code: 'US', name: 'United States' },
  { code: 'UK', name: 'United Kingdom' },
  { code: 'IN', name: 'India' },
  { code: 'AU', name: 'Australia' },
  { code: 'CA', name: 'Canada' },
  // Add more countries as needed
].sort((a, b) => a.name.localeCompare(b.name));

export const ScamAware: React.FC = () => {
  const [selectedCountry, setSelectedCountry] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [scamData, setScamData] = useState<string>(''); // Change to string for markdown
  const [error, setError] = useState<string | null>(null);

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  // Modify the useEffect to handle both initial load and country changes
  useEffect(() => {
    const savedCountry = localStorage.getItem('selectedCountry');
    if (savedCountry && !selectedCountry) {
      setSelectedCountry(savedCountry);
    }
    
    if (selectedCountry) {
      fetchScamData(selectedCountry);
    }
  }, [selectedCountry]); // Only depend on selectedCountry

  const handleCountryChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const country = event.target.value;
    localStorage.setItem('selectedCountry', country);
    setSelectedCountry(country);
    // Remove direct fetchScamData call here since useEffect will handle it
  };

  const fetchScamData = async (country: string) => {
    if (!country) return;

    setIsLoading(true);
    setError(null);

    try {
      const genAI = new GoogleGenerativeAI('AIzaSyDTC-uqSepuy07K7-qoM5_Q4FPRkbb5t84');
      const model = genAI.getGenerativeModel({
        model: "gemini-2.0-flash-exp",
        generationConfig: {
          temperature: 0.9,
          topK: 40,
          topP: 0.95,
          maxOutputTokens: 8192,
        },
      });

      const prompt = `Analyze and provide detailed information about the latest financial scams in ${country}. 
      Format your response as a well-structured markdown document including:

      # Current Financial Scam Alerts
      Brief overview of the current scam situation in ${country}

      ## Recent Investment Scams
      Details about the most recent investment-related scams, their methods, and impact

      ## Common Fraud Techniques
      Explanation of popular techniques scammers are using

      ## Warning Signs
      Key indicators that something might be a scam

      ## Protection Measures
      Steps people can take to protect themselves

      ## Reporting Information
      How to report scams in ${country} and relevant contact information

      Please be detailed and specific to ${country}'s context. Include real examples where possible.`;

      const result = await model.generateContent(prompt);
      const markdown = result.response.text();
      setScamData(markdown);
    } catch (error) {
      setError('Failed to fetch scam data. Please try again later.');
      console.error('Scam data fetch error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxW="container.xl" py={8}>
      <VStack spacing={8} align="stretch">
        <Heading 
          textAlign="center" 
          bgGradient="linear(to-r, red.400, orange.500)"
          bgClip="text"
        >
          Scam Awareness Center
        </Heading>

        <Card bg={bgColor} p={6}>
          <VStack spacing={4}>
            <Text fontSize="lg">Select your country to view recent financial scams:</Text>
            <Select
              placeholder="Choose a country"
              value={selectedCountry}
              onChange={handleCountryChange}
              maxW="400px"
            >
              {countries.map((country) => (
                <option key={country.code} value={country.code}>
                  {country.name}
                </option>
              ))}
            </Select>
          </VStack>
        </Card>

        {error && (
          <Alert status="error">
            <AlertIcon />
            <AlertTitle>Error!</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {isLoading ? (
          <Box textAlign="center" py={10}>
            <Spinner size="xl" />
            <Text mt={4}>Analyzing recent scams in your region...</Text>
          </Box>
        ) : (
          <AnimatePresence>
            {scamData && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <Box
                  bg={useColorModeValue('white', 'gray.800')}
                  p={8}
                  borderRadius="xl"
                  boxShadow="xl"
                  sx={{
                    '& h1': {
                      fontSize: '2xl',
                      fontWeight: 'bold',
                      mb: 6,
                      color: 'red.500',
                    },
                    '& h2': {
                      fontSize: 'xl',
                      fontWeight: 'semibold',
                      mb: 4,
                      mt: 6,
                      color: useColorModeValue('orange.600', 'orange.300'),
                    },
                    '& p': {
                      mb: 4,
                      lineHeight: 1.8,
                    },
                    '& ul': {
                      mb: 4,
                      pl: 6,
                    },
                    '& li': {
                      mb: 2,
                    },
                    '& strong': {
                      color: useColorModeValue('red.600', 'red.300'),
                    },
                  }}
                >
                  <ReactMarkdown>{scamData}</ReactMarkdown>
                </Box>
              </motion.div>
            )}
          </AnimatePresence>
        )}

        {!isLoading && !scamData && selectedCountry && !error && (
          <Text textAlign="center" color="gray.500">
            No scam data available for the selected country.
          </Text>
        )}
      </VStack>
    </Container>
  );
};
